import { CircularProgress, Dialog, DialogContent, useMediaQuery } from '@material-ui/core'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import type { CTAConfigJson } from 'opensolar-checkout'
import { Button, Checkbox, ComponentVersionsInherit, ComponentVersions_3_0, Typography, styled } from 'opensolar-ui'
import React, { useCallback, useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import restClient from 'restClient'
import useTranslateParse from 'util/useTranslateParse'
import { useClooverApplication } from '../cloover/hooks'
export type { CTAConfigJson } from 'opensolar-checkout'

const restClientInstance = restClient(window.API_ROOT + '/api')

type PropTypes = {
  open: boolean
  onClose: () => void
  onContinue?: () => void
  project_id: number
  paymentOptionId: number
  systemUuid: string
  orgId: number
  isFullWidth: boolean
  ctaDialogConfigJson: CTAConfigJson
  integration?: string | null
  source?: string
}

const LogoContainer = styled('div', { name: 'LogoContainer' })({
  textAlign: 'center',
  marginTop: '20px',
})

const Logo = styled('img', { name: 'Logo' })({
  height: '40px',
})

const MainText = styled(Typography)({
  margin: '35px 55px 0',
  textAlign: 'center',
  whiteSpace: 'pre-line',
})

const SubText = styled(Typography)({
  margin: '35px 55px 0',
  textAlign: 'center',
})

const AcceptanceText = styled('div')({
  margin: '35px 55px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const ButtonContainer = styled('div', { name: 'ButtonContainer' })({
  margin: '30px 0 15px',
  display: 'flex',
  gap: '30px',
  justifyContent: 'center',
})

const IframeContainer = styled('div')({
  width: '100%',
  height: '100%',
  minHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
})

const IframeHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const Iframe = styled('iframe')({
  flex: 1,
  border: 'none',
  width: '100%',
  height: '100%',
})

const linkStyle = { color: '#4272DD', textDecoration: 'none' } as const

const DisclaimerRedirectDialog: React.FunctionComponent<PropTypes> = (props) => {
  const translate = useTranslate()
  const translateParse = useTranslateParse()
  const isMobile = useMediaQuery('(max-width:800px)')
  const projectId = props.project_id
  const ctaConfigJson = props.ctaDialogConfigJson
  const notify = useNotify()

  const { checkoutUrl, startClooverCheckout, maybePollClooverSyncOnClose } = useClooverApplication({
    integration: props.integration,
    orgId: props.orgId,
    projectId: props.project_id,
    systemUuid: props.systemUuid,
    paymentOptionId: props.paymentOptionId,
    open: props.open,
  })

  // Check if terms and privacy policy URLs are provided
  const hasTermsUrl = Boolean(ctaConfigJson.terms_url)
  const hasPrivacyPolicyUrl = Boolean(ctaConfigJson.privacy_policy_url)
  const requiresTermsAcceptance = hasTermsUrl || hasPrivacyPolicyUrl

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [acceptTerms, setAcceptTerms] = useState<boolean>(!requiresTermsAcceptance)

  useEffect(() => {
    logAmplitudeEventAllowCustomers('integrated_finance_application_opened', {
      integration: props.integration || 'selina',
      project_id: props.project_id,
      payment_option_id: props?.paymentOptionId,
      source: props.source || 'online_proposal',
    })
  }, [])

  const handleClose = useCallback(() => {
    maybePollClooverSyncOnClose()
    props.onClose()
  }, [maybePollClooverSyncOnClose, props.onClose])

  const submitApplication = async () => {
    setIsLoading(true)
    logAmplitudeEventAllowCustomers('integrated_finance_application_opened', {
      integration: props.integration || 'selina',
      project_id: props.project_id,
      payment_option_id: props?.paymentOptionId,
      source: props.source || 'online_proposal',
    })

    let url = ctaConfigJson.redirect_url

    if (props.integration === 'cloover') {
      const ok = await startClooverCheckout()

      if (ok) {
        setAcceptTerms(false)
      }

      setIsLoading(false)
      return
    } else if (props.integration === 'hometree') {
      try {
        const response = await restClientInstance('CUSTOM_POST', 'custom', {
          url: `orgs/${props.orgId}/projects/${props.project_id}/systems/${props.systemUuid}/${props.paymentOptionId}/hometree/apply_link/`,
        })
        url = response.data.apply_link
      } catch (error: any) {
        notify(translate(error?.body?.error || error?.message), 'error')
        setIsLoading(false)
        return
      }
    } else {
      url = `${url}${projectId}`
    }
    window.open(url, '_blank')
    setAcceptTerms(false)
    setIsLoading(false)

    if (props.onContinue) {
      props.onContinue()
    } else {
      handleClose()
    }
  }

  const confirmBeforeClose = () => {
    if (window.confirm(translate('Are you sure you want to cancel?'))) handleClose()
  }

  const handleCheckboxChange = () => {
    setAcceptTerms((acceptTerms) => !acceptTerms)
  }

  const renderTermsPrivacyAcceptance = () => {
    const termsLink = (label: string) => (
      <a href={ctaConfigJson.terms_url} style={linkStyle} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    )
    const privacyLink = (label: string) => (
      <a href={ctaConfigJson.privacy_policy_url} style={linkStyle} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    )

    if (hasTermsUrl && hasPrivacyPolicyUrl) {
      return translateParse(
        'By checking here, you confirm that you agree with the <a1>terms & conditions<a1> and <a2>privacy policy<a2>.',
        { a1: termsLink, a2: privacyLink }
      )
    }

    if (hasTermsUrl) {
      return translateParse('By checking here, you confirm that you agree with the <a1>terms & conditions<a1>.', {
        a1: termsLink,
      })
    }

    if (hasPrivacyPolicyUrl) {
      return translateParse('By checking here, you confirm that you agree with the <a1>privacy policy<a1>.', {
        a1: privacyLink,
      })
    }

    return ''
  }

  const renderContent = () => {
    if (checkoutUrl) {
      return (
        <IframeContainer>
          <IframeHeader>
            <Typography textVariant="h6">{translate('Cloover Financing Application')}</Typography>
            <Button variant="outlined" onClick={handleClose}>
              Close
            </Button>
          </IframeHeader>
          <Iframe src={checkoutUrl} allow="camera; microphone" title={translate('Cloover Application')} />
        </IframeContainer>
      )
    }

    return (
      <>
        <LogoContainer>
          <Logo src={window.PUBLIC_URL + `${ctaConfigJson.logo_url}`} />
        </LogoContainer>
        <MainText as="div">{ctaConfigJson.main_text}</MainText>
        {ctaConfigJson.sub_text && <SubText textVariant="body1SemiBold">{ctaConfigJson.sub_text}</SubText>}
        {requiresTermsAcceptance && (
          <AcceptanceText>
            <Checkbox checked={acceptTerms} onChange={handleCheckboxChange} />
            <Typography>{renderTermsPrivacyAcceptance()}</Typography>
          </AcceptanceText>
        )}
        <ButtonContainer>
          <Button variant="outlined" onClick={confirmBeforeClose} disabled={isLoading}>
            {translate('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={submitApplication}
            disabled={(requiresTermsAcceptance && !acceptTerms) || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : translate('Continue')}
          </Button>
        </ButtonContainer>
      </>
    )
  }

  const isFullScreen = checkoutUrl ? true : isMobile || props.isFullWidth

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Dialog
        maxWidth={props.isFullWidth || checkoutUrl ? 'xl' : 'sm'}
        open={props.open}
        onClose={confirmBeforeClose}
        fullScreen={isFullScreen}
        fullWidth={isFullScreen}
      >
        <DialogContent dividers={!checkoutUrl}>{renderContent()}</DialogContent>
      </Dialog>
    </ComponentVersionsInherit>
  )
}

export default DisclaimerRedirectDialog
